import { apiFetch } from '@/lib/api/http';

export type MemberStatus = 'active' | 'graduated' | 'withdrawn' | 'expelled';

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  active: '在籍',
  graduated: '卒業',
  withdrawn: '退会',
  expelled: '除名',
};

/**
 * 会員プロフィール。
 * 連絡先（phone / contact_email）は権限が無いロールには **null** で返る（指示書 §6）。
 */
export interface Member {
  id: string;
  member_id: string;
  user_id: string | null;
  name: string;
  name_kana: string | null;
  company: string | null;
  position: string | null;
  /** 管掌領域（例: 事業戦略・IT/SaaS）。任意（D-112。D-148で「専門領域」から改称） */
  specialty: string | null;
  cohort_id: string | null;
  cohort_name: string | null;
  course_id: string | null;
  course_name: string | null;
  bio: string | null;
  /** 企業HP（D-148） */
  company_url: string | null;
  /** 部下人数（D-148） */
  subordinate_count: number | null;
  /** 勤続年数（D-148） */
  years_of_service: number | null;
  /** 趣味・特技・最近のマイブーム（D-148） */
  hobbies: string | null;
  /** 経歴（D-148） */
  career_history: string | null;
  /** CXO Growth参加目的（D-148） */
  participation_purpose: string | null;
  /** 強み（D-148） */
  strengths: string | null;
  /** 弱み（D-148） */
  weaknesses: string | null;
  /** 目標（D-148） */
  goals: string | null;
  photo_url: string | null;
  /** 一覧の担当列（事務局のみ値が入る / D-110 A-5） */
  status: MemberStatus;
  tags: string[];
  phone: string | null;
  contact_email: string | null;
  /** ログインID（User.email）。事務局にのみ返る（D-178） */
  login_email: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

export interface MemberQuery {
  page?: number;
  per_page?: number;
  q?: string;
  status?: MemberStatus | null;
  /** 期で絞り込む（D-176） */
  cohort_id?: string | null;
  /** コースで絞り込む。期とコースはセット（D-114・D-176） */
  course_id?: string | null;
  /** 担当者（理事・講師／サクセス担当）のユーザーIDで絞り込む（D-078） */
  assigned_to?: string | null;
  /**
   * `assigned_to` と併用。サクセス担当の本来業務であるサクセス面談を実施した会員だけに
   * 絞る（理事・講師の月例会等も含む「関わった全部」ではなく面談実施回のみ / D-171）。
   */
  success_meetings_only?: boolean;
}

export function fetchMembers(query: MemberQuery = {}): Promise<Page<Member>> {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.per_page) params.set('per_page', String(query.per_page));
  if (query.q) params.set('q', query.q);
  if (query.status) params.set('status', query.status);
  if (query.cohort_id) params.set('cohort_id', query.cohort_id);
  if (query.course_id) params.set('course_id', query.course_id);
  if (query.assigned_to) params.set('assigned_to', query.assigned_to);
  if (query.success_meetings_only) params.set('success_meetings_only', 'true');
  const search = params.toString();
  return apiFetch<Page<Member>>(`/members${search ? `?${search}` : ''}`);
}

export function fetchMember(id: string): Promise<Member> {
  return apiFetch<Member>(`/members/${id}`);
}

export interface MemberCreateInput {
  /** 未指定ならシステムが自動採番する（D-122） */
  member_id?: string;
  name: string;
  /** 入れると登録と同時に招待メールを送る（D-122） */
  invite_email?: string | null;
  name_kana?: string | null;
  company?: string | null;
  position?: string | null;
  specialty?: string | null;
  cohort_id: string;
  course_id: string;
  bio?: string | null;
  company_url?: string | null;
  subordinate_count?: number | null;
  years_of_service?: number | null;
  hobbies?: string | null;
  career_history?: string | null;
  participation_purpose?: string | null;
  strengths?: string | null;
  weaknesses?: string | null;
  goals?: string | null;
  phone?: string | null;
  contact_email?: string | null;
  tags?: string[];
}

/**
 * 会員の新規登録の応答（D-153）。
 * `invitation_accept_url` は**この応答にしか現れない**（トークンはハッシュで保存するため）。
 * メールを送っていない間は、事務局がこのリンクを本人に直接伝える。
 */
export interface MemberCreated extends Member {
  invitation_accept_url: string | null;
  invitation_mail_sent: boolean;
}

export function createMember(input: MemberCreateInput): Promise<MemberCreated> {
  return apiFetch<MemberCreated>('/members', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export interface MemberUpdateInput {
  name?: string;
  name_kana?: string | null;
  company?: string | null;
  position?: string | null;
  specialty?: string | null;
  cohort_id?: string;
  course_id?: string;
  bio?: string | null;
  company_url?: string | null;
  subordinate_count?: number | null;
  years_of_service?: number | null;
  hobbies?: string | null;
  career_history?: string | null;
  participation_purpose?: string | null;
  strengths?: string | null;
  weaknesses?: string | null;
  goals?: string | null;
  phone?: string | null;
  contact_email?: string | null;
  tags?: string[];
}

export function updateMember(id: string, input: MemberUpdateInput): Promise<Member> {
  return apiFetch<Member>(`/members/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function changeMemberStatus(
  id: string,
  status: MemberStatus,
  reason: string,
): Promise<Member> {
  return apiFetch<Member>(`/members/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, reason }),
  });
}

export function fetchOwnMember(): Promise<Member> {
  return apiFetch<Member>('/members/me');
}

export interface MemberSelfUpdateInput {
  /** 氏名・会社・役職は本人が入力する（変えると事務局に通知が届く / D-122） */
  name?: string;
  name_kana?: string | null;
  company?: string | null;
  position?: string | null;
  specialty?: string | null;
  bio?: string | null;
  company_url?: string | null;
  subordinate_count?: number | null;
  years_of_service?: number | null;
  hobbies?: string | null;
  career_history?: string | null;
  participation_purpose?: string | null;
  strengths?: string | null;
  weaknesses?: string | null;
  goals?: string | null;
  phone?: string | null;
  contact_email?: string | null;
}

export function updateOwnMember(input: MemberSelfUpdateInput): Promise<Member> {
  return apiFetch<Member>('/members/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

// --- 顔写真（D-053）。実体はMinIOに置き、photo_url には短命な閲覧URLが返る -----

/** 受け入れる画像形式（APIと揃える。ファイル選択ダイアログの絞り込みにも使う） */
export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export function uploadOwnPhoto(file: File): Promise<Member> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<Member>('/members/me/photo', { method: 'PUT', body: form });
}

export function deleteOwnPhoto(): Promise<Member> {
  return apiFetch<Member>('/members/me/photo', { method: 'DELETE' });
}

export function uploadMemberPhoto(memberId: string, file: File): Promise<Member> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<Member>(`/members/${memberId}/photo`, { method: 'PUT', body: form });
}

export function deleteMemberPhoto(memberId: string): Promise<Member> {
  return apiFetch<Member>(`/members/${memberId}/photo`, { method: 'DELETE' });
}

// --- あとからの招待（未紐づけの会員 / D-140） ------------------------------

/** 登録時に招待しなかった会員（CSV取込直後など）を、あとから招待する */
export function inviteMember(memberId: string, email: string): Promise<Member> {
  return apiFetch<Member>(`/members/${memberId}/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

// --- ログイン情報の復旧（事務局のみ / D-082） ------------------------------

/** 本人あてに再設定メールを送る（事務局はパスワードを知らないまま復旧できる） */
export function sendMemberPasswordResetEmail(memberId: string): Promise<void> {
  return apiFetch<void>(`/members/${memberId}/password-reset-email`, { method: 'POST' });
}

/** メールが届かない場合の最後の手段。平文はこの応答にしか現れない */
export function issueMemberTemporaryPassword(
  memberId: string,
): Promise<{ temporary_password: string }> {
  return apiFetch<{ temporary_password: string }>(`/members/${memberId}/temporary-password`, {
    method: 'POST',
  });
}

// --- 担当割当（指示書 §5.1 assignments） -----------------------------------
export type AssignmentKind = 'director' | 'success';

export interface Assignment {
  id: string;
  member_id: string;
  assignee_user_id: string;
  assignee_name: string | null;
  assignee_email: string | null;
  kind: AssignmentKind;
  year: number;
  created_at: string;
}

export function fetchAssignments(memberId: string): Promise<Assignment[]> {
  return apiFetch<Assignment[]>(`/members/${memberId}/assignments`);
}

export function createAssignment(
  memberId: string,
  input: { assignee_user_id: string; kind: AssignmentKind; year: number },
): Promise<Assignment> {
  return apiFetch<Assignment>(`/members/${memberId}/assignments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function deleteAssignment(assignmentId: string): Promise<void> {
  return apiFetch<void>(`/assignments/${assignmentId}`, { method: 'DELETE' });
}

/** 会員詳細「イベント」タブ: 出欠履歴（指示書 §7-2・§12 M12）. */
export interface MemberAttendanceEntry {
  event_id: string;
  event_title: string;
  /** 種別名（月例会・1on1 など）。画面では種別ごとに分ける（D-302） */
  event_type_name: string;
  event_type_sort_order: number;
  /** 日時未定の面談がありうる（D-173） */
  starts_at: string | null;
  /** 面談（サクセス面談・1on1）は出欠を使わない（D-177） */
  is_meeting: boolean;
  /** 出欠を取らない回（月例会など / D-333） */
  attendance_not_required: boolean;
  part: 'main' | 'social';
  status: string;
  waitlisted: boolean;
}

export function fetchMemberAttendance(memberId: string): Promise<MemberAttendanceEntry[]> {
  return apiFetch<MemberAttendanceEntry[]>(`/members/${memberId}/attendance`);
}

// --- 参加イベント1件の結果（D-150） ------------------------------------------

export interface MemberEventAnswer {
  question_id: string;
  label: string;
  value: string;
}

export interface MemberEventSurvey {
  event_survey_id: string;
  survey_name: string;
  survey_kind: string;
  /** not_started / draft / submitted */
  status: string;
  submitted_at: string | null;
  deadline_at: string | null;
  answers: MemberEventAnswer[];
}

export interface MemberEventAttendance {
  part: string;
  status: string;
  source: string;
  decided_at: string | null;
  note: string | null;
  waitlisted: boolean;
}

export interface MemberEventAbsence {
  status: string;
  reason: string;
  decided_at: string | null;
  decided_by_name: string | null;
}

export interface MemberEventFeedback {
  is_best_performer: boolean;
  attitude: string | null;
}

export interface MemberEventMeetLog {
  email: string;
  display_name: string | null;
  join_at: string | null;
  leave_at: string | null;
  duration_seconds: number | null;
}

/**
 * その受講生の、そのイベントにおける結果（D-150）。
 * 権限で見えないものは空で返る（席次・分科会FB・Meetログ）。
 */
export interface MemberEventResult {
  member_id: string;
  member_code: string;
  member_name: string;
  event_id: string;
  event_title: string;
  event_type_name: string;
  event_type_code: string;
  /** 日時未定の面談がありうる（D-173） */
  starts_at: string | null;
  ends_at: string | null;
  venue: string | null;
  event_status: string;
  /** 出欠を取らない回（月例会など / D-333） */
  attendance_required: boolean;
  attendance: MemberEventAttendance[];
  absence_request: MemberEventAbsence | null;
  surveys: MemberEventSurvey[];
  table_no: number | null;
  seat_no: number | null;
  feedback: MemberEventFeedback | null;
  meet_logs: MemberEventMeetLog[];
}

export function fetchMemberEventResult(
  memberId: string,
  eventId: string,
): Promise<MemberEventResult> {
  return apiFetch<MemberEventResult>(`/members/${memberId}/events/${eventId}`);
}

// --- Meet突合用の確認済みメール（事務局のみ / implementation-spec §8・D-063） ------

export interface MemberMeetEmail {
  id: string;
  member_id: string;
  email: string;
  verified: boolean;
  slot_no: number;
}

export const MEET_EMAIL_LIMIT = 3;

export function fetchMemberMeetEmails(memberId: string): Promise<MemberMeetEmail[]> {
  return apiFetch<MemberMeetEmail[]>(`/members/${memberId}/meet-emails`);
}

export function addMemberMeetEmail(
  memberId: string,
  email: string,
  verified: boolean,
): Promise<MemberMeetEmail> {
  return apiFetch<MemberMeetEmail>(`/members/${memberId}/meet-emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, verified }),
  });
}

export function setMemberMeetEmailVerified(
  memberId: string,
  meetEmailId: string,
  verified: boolean,
): Promise<MemberMeetEmail> {
  return apiFetch<MemberMeetEmail>(`/members/${memberId}/meet-emails/${meetEmailId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verified }),
  });
}

export function deleteMemberMeetEmail(memberId: string, meetEmailId: string): Promise<void> {
  return apiFetch<void>(`/members/${memberId}/meet-emails/${meetEmailId}`, { method: 'DELETE' });
}
